import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Shield, 
  MapPin, 
  Clock, 
  Car, 
  Navigation, 
  Phone,
  Eye,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Custom marker icons
const originIcon = L.divIcon({
  className: 'custom-div-icon',
  html: `<div style="background-color: #14B8A6; width: 20px; height: 20px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const destinationIcon = L.divIcon({
  className: 'custom-div-icon',
  html: `<div style="background-color: #F59E0B; width: 20px; height: 20px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const vehicleIcon = L.divIcon({
  className: 'custom-div-icon',
  html: `<div style="background-color: #3B82F6; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/></svg>
  </div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

// Status Badge Component
const StatusBadge = ({ status }) => {
  const getConfig = () => {
    switch (status) {
      case 'pending': return { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', label: 'Esperando conductor' };
      case 'accepted': return { color: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Conductor asignado' };
      case 'arrived': return { color: 'bg-teal-100 text-teal-700 border-teal-200', label: 'Conductor llegó' };
      case 'in_progress': return { color: 'bg-green-100 text-green-700 border-green-200', label: 'En camino' };
      case 'completed': return { color: 'bg-slate-100 text-slate-700 border-slate-200', label: 'Completado' };
      case 'cancelled': return { color: 'bg-red-100 text-red-700 border-red-200', label: 'Cancelado' };
      default: return { color: 'bg-slate-100 text-slate-600 border-slate-200', label: status };
    }
  };
  
  const config = getConfig();
  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium border ${config.color}`}>
      {config.label}
    </span>
  );
};

export default function TripFollowPage() {
  const { token } = useParams();
  const [tripData, setTripData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewerId] = useState(() => `viewer_${Date.now()}`);
  const refreshIntervalRef = useRef(null);

  const loadTripData = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/public/trip-follow/${token}`, {
        params: { viewer_id: viewerId }
      });
      setTripData(response.data);
      setError(null);
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        setError({ title: 'Enlace no válido', message: 'Este enlace de seguimiento no existe o ha expirado.' });
      } else if (status === 410) {
        setError({ title: 'Enlace expirado', message: err.response?.data?.detail || 'Este enlace ya no está activo.' });
      } else {
        setError({ title: 'Error', message: 'No se pudo cargar la información del viaje.' });
      }
    } finally {
      setLoading(false);
    }
  }, [token, viewerId]);

  // Initial load
  useEffect(() => {
    loadTripData();
  }, [loadTripData]);

  // Auto-refresh every 10 seconds if trip is live
  useEffect(() => {
    if (tripData?.is_live) {
      refreshIntervalRef.current = setInterval(loadTripData, 10000);
    }
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [tripData?.is_live, loadTripData]);

  const handleContactCentral = () => {
    // WhatsApp link to Central de Monitoreo
    const message = encodeURIComponent(
      `Hola, estoy siguiendo un viaje de Mercapoli Move (ID: ${tripData?.trip_id?.slice(0, 8)}). ¿Pueden ayudarme con información?`
    );
    window.open(`https://wa.me/50255550001?text=${message}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-teal-500 animate-spin mx-auto" />
          <p className="text-slate-600 mt-4">Cargando información del viaje...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">{error.title}</h2>
            <p className="text-slate-600">{error.message}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { origin, destination, current_location, driver, vehicle, security, share_info, status, eta_minutes, distance_km } = tripData;

  // Calculate map bounds
  const bounds = origin && destination ? [
    [Math.min(origin.lat, destination.lat) - 0.01, Math.min(origin.lng, destination.lng) - 0.01],
    [Math.max(origin.lat, destination.lat) + 0.01, Math.max(origin.lng, destination.lng) + 0.01]
  ] : null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Security Header Banner */}
      <div className={`${security?.emergency_active ? 'bg-red-600' : 'bg-teal-600'} text-white py-3 px-4`}>
        <div className="max-w-3xl mx-auto flex items-center justify-center gap-2">
          <Shield className="w-5 h-5" />
          <span className="text-sm font-medium">
            {security?.emergency_active 
              ? security.emergency_status?.message 
              : security?.message
            }
          </span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 space-y-4">
        {/* Logo and Title */}
        <div className="text-center py-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-xl shadow-sm">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
              <Car className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-slate-800">MERCAPOLI</span>
            <span className="text-teal-600 font-bold">MOVE</span>
          </div>
          <p className="text-slate-500 text-sm mt-2">Seguimiento de viaje en tiempo real</p>
        </div>

        {/* Status Card */}
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Estado del viaje</p>
                <StatusBadge status={status} />
              </div>
              <div className="text-right">
                <p className="text-slate-500 text-sm">Compartido por</p>
                <p className="text-slate-800 font-medium">{share_info?.created_by}</p>
              </div>
            </div>
            
            {tripData.is_live && (
              <div className="mt-4 p-3 bg-green-50 border border-green-100 rounded-lg flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-green-700 text-sm font-medium">Viaje en progreso - Actualización en tiempo real</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Map */}
        <Card className="bg-white border-slate-200 overflow-hidden">
          <div className="h-64 sm:h-80">
            {origin && destination ? (
              <MapContainer
                bounds={bounds}
                style={{ height: '100%', width: '100%' }}
                zoomControl={false}
              >
                <TileLayer
                  url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                  attribution='&copy; OpenStreetMap'
                />
                
                {/* Origin marker */}
                <Marker position={[origin.lat, origin.lng]} icon={originIcon}>
                  <Popup>
                    <div className="text-sm">
                      <p className="font-medium">Origen</p>
                      <p className="text-slate-500">{origin.address || 'Punto de recogida'}</p>
                    </div>
                  </Popup>
                </Marker>
                
                {/* Destination marker */}
                <Marker position={[destination.lat, destination.lng]} icon={destinationIcon}>
                  <Popup>
                    <div className="text-sm">
                      <p className="font-medium">Destino</p>
                      <p className="text-slate-500">{destination.address || 'Punto de llegada'}</p>
                    </div>
                  </Popup>
                </Marker>
                
                {/* Current location (vehicle) if in progress */}
                {tripData.is_live && current_location && (
                  <Marker position={[current_location.lat, current_location.lng]} icon={vehicleIcon}>
                    <Popup>
                      <div className="text-sm">
                        <p className="font-medium">Ubicación actual</p>
                        <p className="text-slate-500">Vehículo en movimiento</p>
                      </div>
                    </Popup>
                  </Marker>
                )}
                
                {/* Route line */}
                <Polyline
                  positions={[[origin.lat, origin.lng], [destination.lat, destination.lng]]}
                  color="#14B8A6"
                  weight={3}
                  opacity={0.6}
                  dashArray="10, 10"
                />
              </MapContainer>
            ) : (
              <div className="h-full flex items-center justify-center bg-slate-100">
                <p className="text-slate-400">Mapa no disponible</p>
              </div>
            )}
          </div>
        </Card>

        {/* Trip Details */}
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4 space-y-4">
            {/* Origin & Destination */}
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-teal-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Origen</p>
                  <p className="text-slate-800 font-medium">{origin?.address || 'Punto de recogida'}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Navigation className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Destino</p>
                  <p className="text-slate-800 font-medium">{destination?.address || 'Punto de llegada'}</p>
                </div>
              </div>
            </div>

            {/* ETA & Distance */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100">
              {eta_minutes && (
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Tiempo estimado</p>
                    <p className="text-slate-800 font-bold">{eta_minutes} min</p>
                  </div>
                </div>
              )}
              {distance_km && (
                <div className="flex items-center gap-2">
                  <Navigation className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Distancia</p>
                    <p className="text-slate-800 font-bold">{distance_km.toFixed(1)} km</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Driver & Vehicle Info */}
        {(driver || vehicle) && (
          <Card className="bg-white border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-slate-800 text-sm flex items-center gap-2">
                <Car className="w-4 h-4 text-blue-500" />
                Información del conductor
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center">
                  <Car className="w-7 h-7 text-slate-400" />
                </div>
                <div className="flex-1">
                  {driver && (
                    <>
                      <p className="text-slate-800 font-medium">{driver.first_name}</p>
                      <div className="flex items-center gap-1 text-amber-500">
                        <span className="text-sm">★</span>
                        <span className="text-sm font-medium">{driver.rating?.toFixed(1) || '4.8'}</span>
                      </div>
                    </>
                  )}
                  {!driver && <p className="text-slate-500">Conductor por asignar</p>}
                </div>
                {vehicle && (
                  <div className="text-right">
                    <p className="text-slate-500 text-xs">Placa</p>
                    <p className="text-slate-800 font-bold text-lg">{vehicle.plate}</p>
                    <p className="text-slate-400 text-xs capitalize">{vehicle.type}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Security Info */}
        <Card className={`${security?.emergency_active ? 'bg-red-50 border-red-200' : 'bg-teal-50 border-teal-200'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl ${security?.emergency_active ? 'bg-red-500' : 'bg-teal-500'} flex items-center justify-center`}>
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <p className={`font-bold ${security?.emergency_active ? 'text-red-800' : 'text-teal-800'}`}>
                  {security?.emergency_active ? 'Emergencia Activa' : 'Mercapoli Move protege tu viaje'}
                </p>
                <p className={`text-sm ${security?.emergency_active ? 'text-red-600' : 'text-teal-600'}`}>
                  Central de Monitoreo activa 24/7
                </p>
              </div>
            </div>
            
            <Button
              onClick={handleContactCentral}
              className={`w-full mt-4 ${security?.emergency_active ? 'bg-red-600 hover:bg-red-700' : 'bg-teal-600 hover:bg-teal-700'} text-white`}
              data-testid="contact-central-button"
            >
              <Phone className="w-4 h-4 mr-2" />
              Contactar Central de Mercapoli
            </Button>
          </CardContent>
        </Card>

        {/* Views Counter */}
        <div className="flex items-center justify-center gap-2 text-slate-400 text-sm py-4">
          <Eye className="w-4 h-4" />
          <span>{share_info?.views_count || 0} personas han visto este viaje</span>
        </div>

        {/* Footer */}
        <div className="text-center pb-8">
          <p className="text-slate-400 text-xs">
            © 2025 Mercapoli Move • Central de Seguridad
          </p>
        </div>
      </div>
    </div>
  );
}
